import {
  ACCESS_TIERS,
  CONTENT_OWNER_TYPES,
  CONTENT_VISIBILITY,
  PLATFORM_ROLES,
} from './platformSchema';
import { getOrganizationMemberships, resolvePlatformRole } from './organizationContext';
import { identityEmailsMatch } from './identityEmail';

function getEntitledPackIds(entitlements = []) {
  return new Set(
    (entitlements || [])
      .filter((entitlement) => entitlement?.status !== 'revoked')
      .map((entitlement) => entitlement?.contentPackId)
      .filter(Boolean)
  );
}

function normalizeVisibility(value = '') {
  if (value === 'org' || value === 'school' || value === 'organization') {
    return CONTENT_VISIBILITY.ORGANIZATION;
  }

  if (value === 'platform') {
    return CONTENT_VISIBILITY.PLATFORM;
  }

  if (value === 'assigned_only') {
    return CONTENT_VISIBILITY.ASSIGNED_ONLY;
  }

  return CONTENT_VISIBILITY.PRIVATE;
}

function inferOwnerType(item = {}) {
  if (item.ownerType) return item.ownerType;
  if (item.orgId || item.organizationId || normalizeVisibility(item.visibility) === CONTENT_VISIBILITY.ORGANIZATION) {
    return CONTENT_OWNER_TYPES.ORGANIZATION;
  }
  if (normalizeVisibility(item.visibility) === CONTENT_VISIBILITY.PLATFORM) {
    return CONTENT_OWNER_TYPES.PLATFORM;
  }
  return CONTENT_OWNER_TYPES.INSTRUCTOR;
}

export function canAccessOrganizationDashboard(userData = {}) {
  // Pass 57C (item 4): the org ops dashboard exposes private operational schedule/client data. A
  // platform CONTENT admin (content packs/entitlements) must NOT read it merely by being content admin.
  // Only the platform super admin is global; everyone else needs an actual org membership.
  if (resolvePlatformRole(userData) === PLATFORM_ROLES.PLATFORM_SUPER_ADMIN) return true;
  return getOrganizationMemberships(userData).length > 0;
}

// Pass 87.1 — the ONE platform-authority predicate. NEVER satisfied by an organization role
// (owner/admin/ops/instructor) — those belong to real school users.
export function isPlatformSuperAdmin(userData = {}) {
  return resolvePlatformRole(userData) === PLATFORM_ROLES.PLATFORM_SUPER_ADMIN;
}

// Pass 87.1 — authority for the PLATFORM QA / DEMO PREVIEW capability. Platform super-admin ONLY, so ordinary SAS
// staff can never see or reach the QA preview controls/routes merely by administering a school. QA preview is VIEWER
// CONTEXT, not identity or authority: the selected org/member/participant ids never grant access, the operator's own
// identity is retained, and Firestore still enforces every underlying read (route params are selections, not auth).
export function canUsePlatformQaPreview(userData = {}) {
  return isPlatformSuperAdmin(userData);
}

// Pass 57C-close (items 1/2): the canonical ROLE SETS. OWNER/ADMIN = full org control incl. security,
// billing, roles, calendar-policy, category/capability. OPS additionally runs day-to-day scheduling
// (schedule/bookings/assign/cancel/publish-early/sync) but NEVER gains owner/admin security actions.
export const ORG_MANAGE_ROLES = [PLATFORM_ROLES.ORGANIZATION_OWNER, PLATFORM_ROLES.ORGANIZATION_ADMIN];
export const ORG_OPERATE_ROLES = [PLATFORM_ROLES.ORGANIZATION_OWNER, PLATFORM_ROLES.ORGANIZATION_ADMIN, PLATFORM_ROLES.ORGANIZATION_OPS];

function hasOrgRole(userData, organizationId, roles) {
  return getOrganizationMemberships(userData).some(
    (m) => m.organizationId === organizationId && roles.includes(m.role)
  );
}

// Security / admin control (roles, invites, billing, calendar-policy, category/capability). Owner/admin.
export function canManageOrganization(userData = {}, organizationId = '') {
  if (resolvePlatformRole(userData) === PLATFORM_ROLES.PLATFORM_SUPER_ADMIN) return true;
  return hasOrgRole(userData, organizationId, ORG_MANAGE_ROLES);
}

// Day-to-day operational control (schedule, bookings, assign/reassign, add-work, cancel, publish-early,
// sync). Owner/admin/OPS. This is what lets Sam/Serena run the school without becoming admin.
export function canOperateOrganization(userData = {}, organizationId = '') {
  if (resolvePlatformRole(userData) === PLATFORM_ROLES.PLATFORM_SUPER_ADMIN) return true;
  return hasOrgRole(userData, organizationId, ORG_OPERATE_ROLES);
}

// Pass 58B — OWNER administration is stricter than general Team management. Only the platform super-admin
// or an existing ORGANIZATION OWNER may create/promote an owner, or demote/disable/revoke the owner. A
// mere organization ADMIN can manage Admin/Ops/Instructor but must NEVER touch the owner. Enforced at the
// UI, the app writer, and the Firestore rules.
export function canAdministerOwner(userData = {}, organizationId = '') {
  if (resolvePlatformRole(userData) === PLATFORM_ROLES.PLATFORM_SUPER_ADMIN) return true;
  return hasOrgRole(userData, organizationId, [PLATFORM_ROLES.ORGANIZATION_OWNER]);
}

// ── Pass 58C — WORKSPACE ENTITLEMENT (derive from canonical facts, not legacy userData.role) ──────────
// Two ORTHOGONAL axes: an OPERATIONAL permission (owner/admin/ops) opens the Organisation workspace; a
// TEACHING relationship opens the Instructor workspace. They are independent — an ops user who does not
// teach (Serena) gets Operations only; an ops user who teaches (Sam/Dom/Marc) gets BOTH with ONE identity
// and ONE login. `ops` never implies teaching, and the permission role `instructor` is not the only
// definition of "teaches".

// Does this canonical membership represent a TEACHING relationship? Prefer the explicit isTeachingInstructor
// flag; only when it is absent fall back to the legacy inference (permission role instructor == teaches).
export function isTeachingMembership(m = {}) {
  if (m.isTeachingInstructor === true) return true;
  if (m.isTeachingInstructor === false) return false;
  return m.role === PLATFORM_ROLES.INSTRUCTOR || m.orgRole === PLATFORM_ROLES.INSTRUCTOR;
}

// Organisation workspace = operational permission (owner/admin/ops). When no specific org is supplied,
// entitlement holds if ANY membership grants an operating role (used by the header workspace switcher).
export function canEnterOrganizationWorkspace(userData = {}, organizationId = '') {
  if (organizationId) return canOperateOrganization(userData, organizationId);
  if (resolvePlatformRole(userData) === PLATFORM_ROLES.PLATFORM_SUPER_ADMIN) return true;
  return getOrganizationMemberships(userData).some((m) => ORG_OPERATE_ROLES.includes(m.role));
}

// Instructor workspace = a teaching relationship in the org (active membership with isTeachingInstructor).
// NOT gated by permission role. When organizationId is omitted, any teaching membership qualifies.
export function canEnterInstructorWorkspace(userData = {}, organizationId = '') {
  return getOrganizationMemberships(userData).some(
    (m) => (!organizationId || m.organizationId === organizationId) &&
      String(m.status || 'active') !== 'inactive' &&
      isTeachingMembership(m)
  );
}

// Pass 58G — Personal / "My Coaching" workspace = a genuine STUDENT/personal coaching relationship.
// Being an authenticated human (owner/ops/instructor staff) does NOT grant it — an org-only owner like
// John must NOT see "My Coaching". Narrowest backward-compatible rule: an explicit student role, OR a
// user who qualifies for no operational AND no teaching workspace at all (so they always have a home).
// Preserves existing genuine students (role/platformRole === 'student').
export function canEnterPersonalWorkspace(userData = {}, organizationId = '') {
  if (userData?.role === 'student' || userData?.platformRole === PLATFORM_ROLES.STUDENT) return true;
  return !canEnterOrganizationWorkspace(userData, organizationId) && !canEnterInstructorWorkspace(userData, organizationId);
}

// Pass 82D §22 — STUDENT PREVIEW workspace: an authorized staff viewer (org OWNER/ADMIN, or platform super-admin) may
// inspect the REAL student experience read-only, for QA/operational verification. This is NOT identity impersonation
// and NOT `canEnterPersonalWorkspace` — it never converts staff into a student, and an ordinary instructor/ops or a
// genuine student does NOT get it (kept deliberately narrow; broaden separately if ever needed). When no org is given,
// entitlement holds if ANY membership grants owner/admin (the header switcher). Org-scoped access to a SPECIFIC
// student's data is enforced per-org in the route + Firestore rules — this only gates the workspace's visibility.
export function canPreviewStudentWorkspace(userData = {}, organizationId = '') {
  if (resolvePlatformRole(userData) === PLATFORM_ROLES.PLATFORM_SUPER_ADMIN) return true;
  if (organizationId) return hasOrgRole(userData, organizationId, ORG_MANAGE_ROLES);
  return getOrganizationMemberships(userData).some((m) => ORG_MANAGE_ROLES.includes(m.role));
}

export function canAssignLessons(userData = {}, organizationId = '') {
  // Pass 57C (item 4): PLATFORM_CONTENT_ADMIN is a CONTENT role — it must NOT confer operational school
  // control (assign/reassign/create/cancel/publish/sync). Only the platform super admin is global.
  if (resolvePlatformRole(userData) === PLATFORM_ROLES.PLATFORM_SUPER_ADMIN) return true;
  return hasOrgRole(userData, organizationId, ORG_OPERATE_ROLES); // owner/admin/ops may assign
}

export function canAccessContentItem(
  item = {},
  {
    currentUserId = '',
    currentOrganizationIds = [],
    entitlements = [],
    platformRole = PLATFORM_ROLES.STUDENT,
  } = {}
) {
  if (
    platformRole === PLATFORM_ROLES.PLATFORM_SUPER_ADMIN ||
    platformRole === PLATFORM_ROLES.PLATFORM_CONTENT_ADMIN
  ) {
    return true;
  }

  const ownerType = inferOwnerType(item);
  const visibility = normalizeVisibility(item.accessVisibility || item.visibility);
  const accessTier = item.accessTier || ACCESS_TIERS.FREE;
  const ownerId = item.ownerId || item.organizationId || item.orgId || '';
  const currentOrgSet = new Set(currentOrganizationIds || []);
  const entitledPackIds = getEntitledPackIds(entitlements);

  if (ownerType === CONTENT_OWNER_TYPES.INSTRUCTOR && ownerId === currentUserId) {
    return true;
  }

  if (visibility === CONTENT_VISIBILITY.PRIVATE) {
    return false;
  }

  if (visibility === CONTENT_VISIBILITY.ORGANIZATION && ownerId && currentOrgSet.has(ownerId)) {
    if (accessTier === ACCESS_TIERS.PREMIUM || accessTier === ACCESS_TIERS.ENTERPRISE) {
      return entitledPackIds.has(item.contentPackId);
    }
    return true;
  }

  if (visibility === CONTENT_VISIBILITY.PLATFORM) {
    if (accessTier === ACCESS_TIERS.FREE) return true;
    return entitledPackIds.has(item.contentPackId);
  }

  if (visibility === CONTENT_VISIBILITY.ASSIGNED_ONLY) {
    return Array.isArray(item.assignedUserIds) && item.assignedUserIds.includes(currentUserId);
  }

  return false;
}

export function canManageOrganizationMembers(userData = {}, organizationId = '') {
  return canManageOrganization(userData, organizationId);
}

export function canManageOrganizationMember(userData = {}, organizationId = '') {
  return canManageOrganizationMembers(userData, organizationId);
}

export function canChangeOrganizationMemberRole(userData = {}, organizationId = '') {
  return canManageOrganizationMembers(userData, organizationId);
}

export function canManageOrganizationInvites(userData = {}, organizationId = '') {
  return canManageOrganization(userData, organizationId);
}

export function canViewOrganizationInvites(userData = {}, organizationId = '') {
  return canManageOrganization(userData, organizationId);
}

export function canAcceptOrganizationInvite(
  invite = {},
  { currentUser = null, userData = {}, platformRole = resolvePlatformRole(userData) } = {}
) {
  if (
    platformRole === PLATFORM_ROLES.PLATFORM_SUPER_ADMIN ||
    platformRole === PLATFORM_ROLES.PLATFORM_CONTENT_ADMIN
  ) {
    return true;
  }

  if ((invite.status || 'pending') !== 'pending') {
    return false;
  }

  // Pass 88.4 — compare under the narrow identity canonicalization (gmail.com ≡ googlemail.com), so a
  // signed-in Google user whose Auth email differs only by that alias is correctly recognised as the
  // invitee. This is comparison-only and never broadens to fuzzy matching (different local part / other
  // domains still fail).
  const currentEmail = currentUser?.email || userData?.email || '';
  const inviteEmail = invite.emailNormalized || invite.email || '';

  return identityEmailsMatch(currentEmail, inviteEmail);
}

export function canViewOrganizationContent(userData = {}, organizationId = '') {
  const platformRole = resolvePlatformRole(userData);
  if (
    platformRole === PLATFORM_ROLES.PLATFORM_SUPER_ADMIN ||
    platformRole === PLATFORM_ROLES.PLATFORM_CONTENT_ADMIN
  ) {
    return true;
  }

  return getOrganizationMemberships(userData).some(
    (membership) => membership.organizationId === organizationId
  );
}

export function canEditContentItem(
  item = {},
  {
    currentUserId = '',
    currentOrganizationIds = [],
    userData = {},
  } = {}
) {
  const ownerType = inferOwnerType(item);
  const ownerId = item.ownerId || item.organizationId || item.orgId || '';

  if (ownerType === CONTENT_OWNER_TYPES.INSTRUCTOR) {
    return ownerId === currentUserId;
  }

  if (ownerType === CONTENT_OWNER_TYPES.ORGANIZATION) {
    return currentOrganizationIds.some(
      (organizationId) =>
        organizationId === ownerId &&
        canManageOrganization(userData, organizationId)
    );
  }

  return resolvePlatformRole(userData) === PLATFORM_ROLES.PLATFORM_SUPER_ADMIN;
}

export function canAccessDrillTemplateManagement(userData = {}) {
  const platformRole = resolvePlatformRole(userData);

  if (
    platformRole === PLATFORM_ROLES.PLATFORM_SUPER_ADMIN ||
    platformRole === PLATFORM_ROLES.PLATFORM_CONTENT_ADMIN
  ) {
    return true;
  }

  if (userData?.role === PLATFORM_ROLES.INSTRUCTOR) {
    return true;
  }

  return getOrganizationMemberships(userData).some((membership) =>
    [
      PLATFORM_ROLES.ORGANIZATION_OWNER,
      PLATFORM_ROLES.ORGANIZATION_ADMIN,
      PLATFORM_ROLES.INSTRUCTOR,
    ].includes(membership.role)
  );
}

export function canManageOrganizationContent(userData = {}, organizationId = '') {
  return canManageOrganization(userData, organizationId);
}

export function canManageContentPack(userData = {}) {
  const platformRole = resolvePlatformRole(userData);
  return (
    platformRole === PLATFORM_ROLES.PLATFORM_SUPER_ADMIN ||
      platformRole === PLATFORM_ROLES.PLATFORM_CONTENT_ADMIN
  );
}

export function canAttachContentPack(userData = {}, organizationId = '') {
  return canManageContentPack(userData) || canManageOrganizationContent(userData, organizationId);
}

export function canManageSubscriptionPlans(userData = {}) {
  return canManageContentPack(userData);
}

export function canManageOrganizationSubscription(userData = {}, organizationId = '') {
  return canManageContentPack(userData) || canManageOrganization(userData, organizationId);
}

export function canViewOrganizationCommercialState(userData = {}, organizationId = '') {
  return canManageContentPack(userData) || canManageOrganization(userData, organizationId);
}

export function canViewOrganizationOnboarding(userData = {}, organizationId = '') {
  return canManageOrganization(userData, organizationId);
}

export function canManageOrganizationOnboarding(userData = {}, organizationId = '') {
  return canManageOrganization(userData, organizationId);
}

export function canRequestUpgrade(userData = {}, organizationId = '') {
  return canManageOrganization(userData, organizationId);
}

export function canStartOrganizationTrial(userData = {}) {
  return canManageSubscriptionPlans(userData);
}

export function canViewLockedPremiumState(userData = {}, organizationId = '') {
  return canViewOrganizationContent(userData, organizationId);
}

export function canViewOrganizationAnalytics(userData = {}, organizationId = '') {
  return canManageOrganization(userData, organizationId);
}

export function canViewPlatformAnalytics(userData = {}) {
  return canManageContentPack(userData);
}

export function canViewInstructorWorkloadAnalytics(userData = {}, organizationId = '') {
  return canManageOrganization(userData, organizationId);
}

export function canManageOrganizationPublicProfile(userData = {}, organizationId = '') {
  return canManageOrganization(userData, organizationId);
}

export function canManageInstructorPublicProfile(
  userData = {},
  {
    currentUserId = '',
    instructorUserId = '',
    organizationId = '',
    // Pass 88.3 §8 — the signed-in user OWNS this canonical instructor membership (resolved by the caller
    // from userData.organizationMemberships via authUserId/userId). The instructor business identity
    // (`instructorUserId` = member.userId) is NEVER the Auth uid, so self-edit is proven by ownership of the
    // membership, not by `currentUserId === instructorUserId`.
    isSelf = false,
  } = {}
) {
  if (canManageContentPack(userData)) return true;
  if (isSelf) return true;
  // Legacy fallback (pre-88.3 data where a member's business userId happened to equal the Auth uid).
  if (currentUserId && instructorUserId && currentUserId === instructorUserId) return true;
  return canManageOrganization(userData, organizationId);
}

export function canViewPublicOrganizationProfile(profile = {}) {
  return Boolean(profile?.isPublic && profile?.profileStatus === 'published');
}

export function canViewPublicInstructorProfile(profile = {}) {
  return Boolean(profile?.isPublic && profile?.profileStatus === 'published');
}

export function canViewPublicService(service = {}) {
  return Boolean(service?.active && service?.visibility === 'public');
}

export function canBookService(service = {}) {
  return canViewPublicService(service) && (service.bookingMode || 'request') === 'request';
}

export function canManageServices(userData = {}, organizationId = '') {
  return canManageOrganization(userData, organizationId);
}

export function canEditOrganizationService(userData = {}, organizationId = '') {
  return canManageServices(userData, organizationId);
}

export function canEditInstructorService(
  userData = {},
  {
    currentUserId = '',
    instructorId = '',
    organizationId = '',
  } = {}
) {
  if (currentUserId && instructorId && currentUserId === instructorId) return true;
  return canManageServices(userData, organizationId);
}

function isOrganizationStaff(userData = {}, organizationId = '') {
  return getOrganizationMemberships(userData).some(
    (membership) =>
      membership.organizationId === organizationId &&
      [
        PLATFORM_ROLES.ORGANIZATION_OWNER,
        PLATFORM_ROLES.ORGANIZATION_ADMIN,
        PLATFORM_ROLES.INSTRUCTOR,
      ].includes(membership.role)
  );
}

export function canViewClientProfile(
  userData = {},
  {
    currentUserId = '',
    organizationId = '',
    clientUserId = '',
  } = {}
) {
  if (canManageContentPack(userData)) return true;
  if (currentUserId && clientUserId && currentUserId === clientUserId) return true;
  return isOrganizationStaff(userData, organizationId);
}

export function canEditClientProfile(
  userData = {},
  {
    organizationId = '',
  } = {}
) {
  return isOrganizationStaff(userData, organizationId);
}

export function canAddClientNote(
  userData = {},
  {
    organizationId = '',
  } = {}
) {
  return isOrganizationStaff(userData, organizationId);
}

export function canViewClientRevenue(
  userData = {},
  {
    organizationId = '',
  } = {}
) {
  return canManageOrganization(userData, organizationId);
}

export function canViewClientTimeline(
  userData = {},
  {
    currentUserId = '',
    organizationId = '',
    clientUserId = '',
  } = {}
) {
  return canViewClientProfile(userData, {
    currentUserId,
    organizationId,
    clientUserId,
  });
}

export function canViewRetentionQueues(userData = {}, organizationId = '') {
  return isOrganizationStaff(userData, organizationId);
}

export function canViewRetentionWorkspace(userData = {}, organizationId = '') {
  return canViewRetentionQueues(userData, organizationId);
}

export function canManageRetentionActions(userData = {}, organizationId = '') {
  return isOrganizationStaff(userData, organizationId);
}

export function canLogRetentionAction(userData = {}, organizationId = '') {
  return canManageRetentionActions(userData, organizationId);
}

export function canViewInstructorCRM(
  userData = {},
  {
    currentUserId = '',
    instructorId = '',
    organizationId = '',
  } = {}
) {
  if (currentUserId && instructorId && currentUserId === instructorId) return true;
  return isOrganizationStaff(userData, organizationId);
}

export function canEditClientSummary(
  userData = {},
  {
    organizationId = '',
  } = {}
) {
  return canEditClientProfile(userData, { organizationId });
}

export function canViewClientRecommendations(
  userData = {},
  {
    currentUserId = '',
    organizationId = '',
    clientUserId = '',
  } = {}
) {
  return canViewClientProfile(userData, {
    currentUserId,
    organizationId,
    clientUserId,
  });
}

export function canViewServiceRetentionAnalytics(userData = {}, organizationId = '') {
  return isOrganizationStaff(userData, organizationId);
}

export function canUseAIAssistDrafts(userData = {}, organizationId = '') {
  return isOrganizationStaff(userData, organizationId);
}

export function canSnoozeRetentionRecommendation(userData = {}, organizationId = '') {
  return isOrganizationStaff(userData, organizationId);
}

export function canViewConversionFunnels(userData = {}, organizationId = '') {
  return isOrganizationStaff(userData, organizationId);
}

export function canViewRetentionActionHistory(
  userData = {},
  {
    currentUserId = '',
    organizationId = '',
    clientUserId = '',
  } = {}
) {
  return canViewClientProfile(userData, {
    currentUserId,
    organizationId,
    clientUserId,
  });
}

export function canManageRetentionTemplates(userData = {}, organizationId = '') {
  return isOrganizationStaff(userData, organizationId);
}

export function canEditRetentionTemplate(userData = {}, organizationId = '') {
  return canManageRetentionTemplates(userData, organizationId);
}

export function canArchiveRetentionTemplate(userData = {}, organizationId = '') {
  return canManageRetentionTemplates(userData, organizationId);
}

export function canViewRetentionReporting(userData = {}, organizationId = '') {
  return isOrganizationStaff(userData, organizationId);
}

export function canViewRetentionReportingPage(userData = {}, organizationId = '') {
  return canViewRetentionReporting(userData, organizationId);
}

export function canManageBulkRetentionActions(userData = {}, organizationId = '') {
  return isOrganizationStaff(userData, organizationId);
}

export function canManageBulkInstructorReassignment(userData = {}, organizationId = '') {
  return canManageBulkRetentionActions(userData, organizationId);
}

export function canManageBulkClientNotes(userData = {}, organizationId = '') {
  return canManageBulkRetentionActions(userData, organizationId);
}

export function canViewStudentProgressDashboard(
  userData = {},
  {
    currentUserId = '',
    targetUserId = '',
    organizationId = '',
  } = {}
) {
  if (currentUserId && targetUserId && currentUserId === targetUserId) return true;
  return organizationId ? isOrganizationStaff(userData, organizationId) : false;
}

export function canViewPackageRecommendations(
  userData = {},
  {
    currentUserId = '',
    targetUserId = '',
    isPublic = false,
    organizationId = '',
  } = {}
) {
  if (isPublic) return true;
  return canViewStudentProgressDashboard(userData, {
    currentUserId,
    targetUserId,
    organizationId,
  });
}

export function canViewProgressMilestones(
  userData = {},
  {
    currentUserId = '',
    targetUserId = '',
    organizationId = '',
  } = {}
) {
  return canViewStudentProgressDashboard(userData, {
    currentUserId,
    targetUserId,
    organizationId,
  });
}

export function canUseRebookRecommendation(
  userData = {},
  {
    currentUserId = '',
    targetUserId = '',
    organizationId = '',
  } = {}
) {
  return canViewStudentProgressDashboard(userData, {
    currentUserId,
    targetUserId,
    organizationId,
  });
}

export function canViewStudentPackageSelection(
  userData = {},
  {
    currentUserId = '',
    targetUserId = '',
    organizationId = '',
  } = {}
) {
  return canViewPackageRecommendations(userData, {
    currentUserId,
    targetUserId,
    organizationId,
  });
}

export function canCreateBookingRequest() {
  return true;
}

export function canManageOrganizationBookings(userData = {}, organizationId = '') {
  return canManageOrganization(userData, organizationId);
}

export function canManageInstructorBookings(
  userData = {},
  {
    currentUserId = '',
    instructorId = '',
    organizationId = '',
  } = {}
) {
  if (canManageOrganization(userData, organizationId)) return true;
  return Boolean(currentUserId && instructorId && currentUserId === instructorId);
}

export function canViewBookingRequest(
  bookingRequest = {},
  {
    currentUserId = '',
    userData = {},
  } = {}
) {
  if (canManageOrganizationBookings(userData, bookingRequest.organizationId)) return true;
  if (bookingRequest.instructorId && bookingRequest.instructorId === currentUserId) return true;
  if (bookingRequest.requesterUserId && bookingRequest.requesterUserId === currentUserId) return true;
  return false;
}

export function canManageAvailability(
  userData = {},
  {
    currentUserId = '',
    instructorId = '',
    organizationId = '',
  } = {}
) {
  return canManageInstructorBookings(userData, {
    currentUserId,
    instructorId,
    organizationId,
  });
}

export function canViewBooking(
  booking = {},
  {
    currentUserId = '',
    userData = {},
  } = {}
) {
  if (canManageOrganizationBookings(userData, booking.organizationId || '')) return true;
  if (booking.instructorId && booking.instructorId === currentUserId) return true;
  if (booking.requesterUserId && booking.requesterUserId === currentUserId) return true;
  return false;
}

export function canManageBooking(
  userData = {},
  {
    currentUserId = '',
    booking = {},
  } = {}
) {
  if (!booking) return false;
  if (canManageOrganizationBookings(userData, booking.organizationId || '')) return true;
  return Boolean(booking.instructorId && booking.instructorId === currentUserId);
}

export function canConfirmBooking(
  userData = {},
  {
    currentUserId = '',
    bookingRequest = {},
  } = {}
) {
  if (!bookingRequest) return false;
  if (canManageOrganizationBookings(userData, bookingRequest.organizationId || '')) return true;
  return Boolean(bookingRequest.instructorId && bookingRequest.instructorId === currentUserId);
}

export function canRescheduleBooking(
  userData = {},
  {
    currentUserId = '',
    booking = {},
  } = {}
) {
  return canManageBooking(userData, { currentUserId, booking });
}

export function canEditBookingSchedule(
  userData = {},
  {
    currentUserId = '',
    booking = {},
    bookingRequest = {},
  } = {}
) {
  if (bookingRequest && (bookingRequest.bookingRequestId || bookingRequest.id)) {
    return canConfirmBooking(userData, {
      currentUserId,
      bookingRequest,
    });
  }

  return canRescheduleBooking(userData, {
    currentUserId,
    booking,
  });
}

export function canViewRequesterBookings(currentUserId = '') {
  return Boolean(currentUserId);
}

export function canRequestBookingChange(
  booking = {},
  {
    currentUserId = '',
  } = {}
) {
  return Boolean(booking?.requesterUserId && currentUserId && booking.requesterUserId === currentUserId);
}

export function canManageBookingChangeRequests(
  userData = {},
  {
    currentUserId = '',
    booking = {},
  } = {}
) {
  return canManageBooking(userData, { currentUserId, booking });
}

export function canViewBookingCommercialState(
  booking = {},
  {
    currentUserId = '',
    userData = {},
  } = {}
) {
  return canViewBooking(booking, { currentUserId, userData });
}

export function canManageBookingCalendarState(
  userData = {},
  {
    currentUserId = '',
    booking = {},
  } = {}
) {
  return canManageBooking(userData, { currentUserId, booking });
}

export function canManageCalendarSync(
  userData = {},
  {
    currentUserId = '',
    booking = {},
  } = {}
) {
  return canManageBookingCalendarState(userData, { currentUserId, booking });
}

export function canViewBookingCalendarState(
  booking = {},
  {
    currentUserId = '',
    userData = {},
  } = {}
) {
  return canViewBooking(booking, { currentUserId, userData });
}

export function canTriggerBookingCalendarSync(
  userData = {},
  {
    currentUserId = '',
    booking = {},
  } = {}
) {
  return canManageCalendarSync(userData, { currentUserId, booking });
}

export function canManageCalendarConnections(
  userData = {},
  {
    currentUserId = '',
    ownerType = '',
    ownerId = '',
    organizationId = '',
  } = {}
) {
  if (canManageContentPack(userData)) return true;

  if (ownerType === 'organization') {
    return canManageOrganization(userData, ownerId || organizationId);
  }

  if (ownerType === 'instructor' || ownerType === 'user') {
    if (currentUserId && ownerId && currentUserId === ownerId) return true;
    return canManageOrganization(userData, organizationId);
  }

  return false;
}

export function canViewCalendarConnections(
  userData = {},
  {
    currentUserId = '',
    ownerType = '',
    ownerId = '',
    organizationId = '',
  } = {}
) {
  return canManageCalendarConnections(userData, {
    currentUserId,
    ownerType,
    ownerId,
    organizationId,
  });
}

export function canUseCalendarConnectionForBooking(
  userData = {},
  {
    currentUserId = '',
    booking = {},
    connection = {},
  } = {}
) {
  if (!connection) return false;

  return canManageCalendarConnections(userData, {
    currentUserId,
    ownerType: connection.ownerType,
    ownerId: connection.ownerId,
    organizationId: booking.organizationId || connection.organizationId || '',
  }) && canManageCalendarSync(userData, { currentUserId, booking });
}

export function canViewCalendarConnectionErrors(
  userData = {},
  {
    currentUserId = '',
    ownerType = '',
    ownerId = '',
    organizationId = '',
  } = {}
) {
  return canViewCalendarConnections(userData, {
    currentUserId,
    ownerType,
    ownerId,
    organizationId,
  });
}

export function canViewBookingPayment(
  payment = {},
  {
    currentUserId = '',
    userData = {},
  } = {}
) {
  if (canManageOrganization(userData, payment.organizationId || '')) return true;
  return Boolean(
    (payment.requesterUserId && payment.requesterUserId === currentUserId) ||
    (payment.bookingRequesterUserId && payment.bookingRequesterUserId === currentUserId)
  );
}

export function canManageBookingPayment(
  userData = {},
  {
    currentUserId = '',
    payment = {},
    booking = {},
  } = {}
) {
  const organizationId = payment.organizationId || booking.organizationId || '';
  if (canManageOrganization(userData, organizationId)) return true;
  return Boolean(booking.instructorId && booking.instructorId === currentUserId);
}

export function canInitiateBookingCheckout(
  booking = {},
  {
    currentUserId = '',
  } = {}
) {
  return Boolean(booking?.requesterUserId && booking.requesterUserId === currentUserId);
}

export function canWaiveBookingPayment(
  userData = {},
  {
    payment = {},
    booking = {},
  } = {}
) {
  return canManageOrganization(userData, payment.organizationId || booking.organizationId || '');
}

export function canApplyBookingCoverage(
  userData = {},
  {
    payment = {},
    booking = {},
  } = {}
) {
  return canManageOrganization(userData, payment.organizationId || booking.organizationId || '');
}

export function canManageServicePackages(userData = {}, organizationId = '') {
  return canManageOrganization(userData, organizationId);
}

export function canAssignPackageCoverage(userData = {}, organizationId = '') {
  return canManageOrganization(userData, organizationId);
}

export function canViewOwnPackageCoverage(
  membership = {},
  {
    currentUserId = '',
    userData = {},
  } = {}
) {
  if (canManageOrganization(userData, membership.organizationId || '')) return true;
  return Boolean(membership.userId && currentUserId && membership.userId === currentUserId);
}

export function canViewOrganizationPackageUsage(userData = {}, organizationId = '') {
  return canManageOrganization(userData, organizationId);
}

export function canApplyPackageToBooking(userData = {}, organizationId = '') {
  return canManageOrganization(userData, organizationId);
}

export function canViewBookingLinkedLesson(
  booking = {},
  {
    currentUserId = '',
    userData = {},
  } = {}
) {
  return canViewBooking(booking, { currentUserId, userData });
}

export function canViewBookingLinkedVideo(
  booking = {},
  {
    currentUserId = '',
    userData = {},
  } = {}
) {
  return canViewBooking(booking, { currentUserId, userData });
}
